import { lazy, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import Inventory from '@/pages/Inventory';
import AuthCallback from '@/pages/AuthCallback';
import NotFound from '@/pages/NotFound';
import { AdminRoute } from '@/components/ProtectedRoute';
import { KioskShell } from '@/components/KioskShell';
import { useUIStore } from '@/store/uiStore';
import { RETAIL_MODE } from '@/lib/retailMode';
import { MVP_FEATURES } from '@/lib/mvpFeatures';

const PublicProfile = lazy(() => import('@/pages/PublicProfile'));
const CardShowDetail = lazy(() => import('@/pages/CardShowDetail'));
const CardShowsIndex = lazy(() => import('@/pages/CardShowsIndex'));
const GameStoreDetail = lazy(() => import('@/pages/GameStoreDetail'));
const ItemDetail = lazy(() => import('@/pages/ItemDetail'));
const Admin = lazy(() => import('@/pages/Admin'));
const EbayTemplates = lazy(() => import('@/pages/settings/EbayTemplates'));
const Profile = lazy(() => import('@/pages/Profile'));
const Privacy = lazy(() => import('@/pages/Privacy'));
const Terms = lazy(() => import('@/pages/Terms'));
const Disclaimer = lazy(() => import('@/pages/Disclaimer'));
const Safety = lazy(() => import('@/pages/Safety'));
const Contact = lazy(() => import('@/pages/Contact'));
const LogoExport = lazy(() => import('@/pages/LogoExport'));
const OfferAction = lazy(() => import('@/pages/OfferAction'));
const Install = lazy(() => import('@/pages/Install'));
const EbaySetupGuide = lazy(() => import('@/pages/EbaySetupGuide'));
const EbayOrders = lazy(() => import('@/pages/EbayOrders'));
const EbayListings = lazy(() => import('@/pages/EbayListings'));
const TransactionHistory = lazy(() => import('@/pages/TransactionHistory'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
const Notifications = lazy(() => import('@/pages/Notifications'));
const MenusAndShortcuts = lazy(() => import('@/pages/MenusAndShortcuts'));
const TilesLab = lazy(() => import('@/pages/_lab/TilesLab'));
const MenuEditor = lazy(() => import('@/pages/MenuEditor'));
const MenuPage = lazy(() => import('@/pages/MenuPage'));
const PreRegisterConfirm = lazy(() => import('@/pages/PreRegisterConfirm'));
const OrderDetailPage = lazy(() => import('@/pages/OrderDetailPage'));
const SellerOrdersList = lazy(() => import('@/pages/SellerOrdersList'));
const SellerOrderDetail = lazy(() => import('@/pages/SellerOrderDetail'));
const ShopSettings = lazy(() => import('@/pages/ShopSettings'));
const SellerPayouts = lazy(() => import('@/pages/SellerPayouts'));

// Redirect component that opens modal and navigates
function AddItemRedirect() {
	const navigate = useNavigate();
	const { openAddItemModal } = useUIStore();

	useEffect(() => {
		openAddItemModal();
		navigate('/inventory', { replace: true });
	}, [openAddItemModal, navigate]);

	return null;
}

/**
 * Permanent client redirect from legacy /discover URLs to /market.
 * Preserves search params (?q=&sort=&lens=) so back-nav and shared links keep working.
 */
function DiscoverLegacyRedirect({ to = '/market' }: { to?: string }) {
	const loc = useLocation();
	return <Navigate to={`${to}${loc.search}${loc.hash}`} replace />;
}

/**
 * Signed-in `/` resolver: skip mounting <Index> entirely (it would fetch an item
 * count and then <Navigate> to /inventory, causing a visible double-load flash).
 * Preserve the pendingCollectrImport deep-link flag.
 */
function AuthedRootRedirect() {
	const pendingCollectr =
		typeof window !== 'undefined' && sessionStorage.getItem('pendingCollectrImport') === '1';
	if (pendingCollectr) {
		sessionStorage.removeItem('pendingCollectrImport');
		return <Navigate to="/inventory?import=true" replace />;
	}
	return <Navigate to="/inventory" replace />;
}

/**
 * Signed-in full app. Caller wraps in <NonAdminGate> so retail-mode
 * non-owners can't slip past.
 */
export function AuthedRoutes() {
	return (
		<Routes>
			{/* Shareable single-store kiosk — also reachable while signed in for preview */}
			<Route path="/s/:username/*" element={<KioskShell />} />
			<Route path="/" element={<AuthedRootRedirect />} />
			<Route path="/dashboard" element={<Navigate to="/inventory" replace />} />
			<Route path="/pre-register/confirm" element={<PreRegisterConfirm />} />

			<Route path="/inventory" element={<Inventory />} />
			<Route path="/inventory/add" element={<AddItemRedirect />} />
			<Route path="/item/:id" element={<ItemDetail />} />
			{/* Cart hidden for MVP — /cart + /order/success redirect to home */}
			<Route path="/cart" element={<Navigate to="/" replace />} />
			<Route path="/order/success" element={<Navigate to="/" replace />} />
			<Route path="/order/:shortId" element={<OrderDetailPage />} />
			{/* Seller commerce dashboard */}
			<Route path="/seller/orders" element={<SellerOrdersList />} />
			<Route path="/seller/orders/:shortId" element={<SellerOrderDetail />} />
			<Route path="/seller/shop-settings" element={<ShopSettings />} />
			<Route path="/seller/payouts" element={<SellerPayouts />} />
			{/* Market hidden during MVP — redirect everything to /inventory */}
			<Route path="/market" element={<DiscoverLegacyRedirect to="/inventory" />} />
			<Route path="/market/*" element={<DiscoverLegacyRedirect to="/inventory" />} />
			<Route path="/marketplace" element={<DiscoverLegacyRedirect to="/inventory" />} />
			<Route path="/marketplace/*" element={<DiscoverLegacyRedirect to="/inventory" />} />
			<Route path="/discover" element={<DiscoverLegacyRedirect to="/inventory" />} />
			<Route path="/discover/*" element={<DiscoverLegacyRedirect to="/inventory" />} />
			<Route path="/sellers" element={<DiscoverLegacyRedirect to="/inventory" />} />
			<Route path="/sellers/*" element={<DiscoverLegacyRedirect to="/inventory" />} />
			<Route path="/u/:username/menu/:id" element={<MenuPage />} />
			<Route path="/u/:username" element={<PublicProfile />} />
			<Route path="/u/:username/g/:gameSlug" element={<PublicProfile />} />
			{RETAIL_MODE && <Route path="/g/:gameSlug" element={<PublicProfile />} />}
			<Route path="/preview" element={<PublicProfile ownerPreview />} />

			<Route path="/shows" element={<CardShowsIndex />} />
			<Route path="/show/:slug" element={<CardShowDetail />} />
			<Route path="/store/:storeId" element={<GameStoreDetail />} />
			<Route path="/settings" element={<Profile />} />
			<Route path="/profile" element={<Navigate to="/settings" replace />} />
			<Route path="/settings/ebay-templates" element={<EbayTemplates />} />
			<Route path="/activity" element={<Navigate to="/transactions" replace />} />
			{/* Once authed, /owner is already "done" — bounce to the inventory. */}
			<Route path="/owner" element={<Navigate to="/inventory" replace />} />

			<Route path="/transactions" element={<TransactionHistory />} />
			<Route path="/ebay-setup" element={<EbaySetupGuide />} />
			<Route path="/orders" element={<EbayOrders />} />
			<Route path="/ebay/listings" element={<EbayListings />} />
			<Route path="/offer/:token" element={<OfferAction />} />
			<Route path="/install" element={<Install />} />

			<Route path="/notifications" element={<Notifications />} />
			<Route path="/menus" element={<MenusAndShortcuts />} />
			{MVP_FEATURES.menus ? (
				<Route path="/menus/:id" element={<MenuEditor />} />
			) : (
				<Route path="/menus/:id" element={<Navigate to="/menus" replace />} />
			)}
			<Route path="/shortcuts" element={<Navigate to="/menus?tab=shortcuts" replace />} />
			<Route path="/logo-export" element={<LogoExport />} />

			<Route path="/lab/tiles" element={<TilesLab />} />
			<Route
				path="/admin"
				element={
					<AdminRoute>
						<Admin />
					</AdminRoute>
				}
			/>
			<Route path="/auth/callback" element={<AuthCallback />} />
			<Route path="/reset-password" element={<ResetPassword />} />
			<Route path="/terms" element={<Terms />} />
			<Route path="/privacy" element={<Privacy />} />
			<Route path="/safety" element={<Safety />} />
			<Route path="/disclaimer" element={<Disclaimer />} />
			<Route path="/contact" element={<Contact />} />
			<Route path="*" element={<NotFound />} />
		</Routes>
	);
}
